import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import Checklist from './Checklist';
import BenchView from './BenchView';
import WorkJournal from './WorkJournal';
import TodayBoard from './TodayBoard';
import { WORKBENCH_BACKGROUNDS, backgroundById, GLASS } from './workbenchBackgrounds';
import { SurfaceContext } from './effortUi';
import { useTimeTotals, TimeBadge } from './timeTotals';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';

// A wall clock for the shop floor, from the computer's own time (2 Oct).
// Ticks on the minute boundary rather than every second: nothing on it shows
// seconds, and a re-render a second is wasted work on a tablet.
function WorkbenchClock({ T }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let id;
    const tick = () => {
      setNow(new Date());
      id = setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
    };
    id = setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
    return () => clearTimeout(id);
  }, []);
  return (
    <div aria-label="Current time" style={{ marginLeft:'auto', textAlign:'right', alignSelf:'center', paddingRight:4 }}>
      <div style={{ fontFamily: DISPLAY_FONT, fontSize:40, fontWeight:700, lineHeight:1, color:T.text, fontVariantNumeric:'tabular-nums', letterSpacing:'-0.02em' }}>
        {now.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}
      </div>
      <div style={{ fontSize:14, color:T.textSub, marginTop:6 }}>
        {now.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
      </div>
    </div>
  );
}

// The large type on the cards. Loaded in public/index.html.
const DISPLAY_FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

// One ticket on the Workbench board. The whole card is the control: tap it to
// open the bench, where the timer, checklist and status live; drag it between
// the columns to say whether it is still on your plate. No buttons on the card
// (decided 2 Oct): every action happens on the bench.
//
// Redrawn 2 Oct: the customer and the bike are the large type, the red and
// orange risk stripes are gone. The only colour a card carries on its own is a
// green ring while a timer runs on it.
function WorkbenchCard({ t, progress, checklistData, checklistRecord, doneRecord, running, paused, timeTotal, side, dragging, T, isDark,
  onOpen, onPointerDown }) {
  const isRunning = running && running.length > 0;
  // Started and left paused (or stopped automatically), nothing running now.
  // Amber, on In Progress only: on a Completed card a pause is history.
  const isPaused = !isRunning && side === 'left' && paused && paused.length > 0;
  const allAuto = isPaused && paused.every(p => p.reason === 'auto');
  const fmtWhen = (d) => d ? new Date(d).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : '';
  const fmtDay = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric' }) : '';
  // Name and colour apart when the queue row carries them, else the joined label.
  const bikeName = t.bikeName || (t.bikeColor ? '' : t.bike) || '—';
  const bikeColor = t.bikeColor || '';
  const subtle = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.6)';
  const chip = { display:'inline-flex', alignItems:'center', gap:5, fontSize:12, padding:'3px 9px', borderRadius:999,
    background: subtle, color: T.textSub, whiteSpace:'nowrap' };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${t.displayId}, ${t.customer}, ${t.bike}. Open the bench.`}
      onClick={() => onOpen(t)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(t); } }}
      onPointerDown={e => onPointerDown(e, t, side)}
      onContextMenu={e => e.preventDefault()}
      className={`wb-card${side === 'right' ? ' wb-done' : ''}${isRunning ? ' wb-running' : ''}${isPaused ? ' wb-paused' : ''}`}
      style={{
        borderRadius: 12,
        padding: '12px 14px 12px',
        userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
        opacity: dragging ? 0.35 : 1,
        outline: 'none',
        position: 'relative',
      }}>

      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap', paddingRight:18 }}>
        <span style={{ fontFamily: DISPLAY_FONT, fontSize:12, fontWeight:700, color:'#1A2535',
          background:'#E67E22', borderRadius:5, padding:'2px 7px', flexShrink:0, fontVariantNumeric:'tabular-nums' }}>{t.displayId}</span>
        <span style={S.statusPill(t.status)}>{t.status}</span>
      </div>

      {/* Grip: says the card can be picked up. Decoration only. */}
      <svg aria-hidden="true" width="10" height="16" viewBox="0 0 10 16" style={{ position:'absolute', top:14, right:12, opacity:0.45 }}>
        {[2, 8].map(x => [2, 8, 14].map(y => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill={T.textSub} />))}
      </svg>

      <div style={{ fontFamily: DISPLAY_FONT, fontSize:18, fontWeight:700, letterSpacing:'-0.01em', color:T.text, lineHeight:1.2 }}>
        {t.customer}
      </div>
      <div style={{ fontFamily: DISPLAY_FONT, fontSize:15, fontWeight:600, color: isDark ? '#b9c6dd' : '#3b4a63', lineHeight:1.3, marginTop:2 }}>
        {bikeName}{bikeColor ? <><span style={{ color:T.textSub, fontWeight:400 }}> · </span>{bikeColor}</> : null}
      </div>

      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
        <span style={chip}><span style={{ fontWeight:600, color:T.text }}>{t.tech}</span></span>
        {t.daysOpen != null && <span style={chip}>Open {t.daysOpen}d</span>}
        {/* Total time, everyone's. Plain here: the running chip below already
            says who is on it, so the badge does not repeat the green. */}
        <TimeBadge total={timeTotal && { ...timeTotal, running: false }} isDark={isDark}
          style={{ fontSize:12, padding:'3px 9px', fontWeight:600, color:T.text }} />
        {t.etaOut && (
          <span style={chip}>
            {t.isOverdue && <span style={{ width:7, height:7, borderRadius:'50%', background:'#e74c3c' }} />}
            {t.isOverdue ? 'Past due ' : 'Due '}{fmtDay(t.etaOut)}
          </span>
        )}
        {isRunning && (
          <span style={{ ...chip, fontWeight:600, color:'#1e9e57', background: isDark ? '#0f2a15' : '#d4edda' }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#2ecc71', animation:'sbTimerPulse 1.4s ease-in-out infinite' }} />
            Timer running · {running.map(r => r.name).join(', ')}
          </span>
        )}
        {isPaused && (
          <span style={{ ...chip, fontWeight:600, color: isDark ? '#f0a35e' : '#a8560f', background: isDark ? '#2e1f0e' : '#fbe5cf' }}
            title={paused.map(p => `${p.name}: ${p.reason === 'auto' ? 'stopped automatically' : 'paused'} ${fmtWhen(p.endedAt)}`).join('\n')}>
            <span aria-hidden="true" style={{ fontSize:10, letterSpacing:'-1px' }}>❚❚</span>
            {allAuto ? 'Auto-stopped' : 'Paused'} · {paused.map(p => p.name).join(', ')}
          </span>
        )}
      </div>

      {side === 'left' && progress && (
        <div style={{ marginTop:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:T.textSub, marginBottom:5 }}>
            <span>{checklistData?.templateName || 'Checklist'}</span>
            <span style={{ fontWeight:600, color:T.text }}>{progress.done}/{progress.total}</span>
          </div>
          <div style={{ height:5, borderRadius:3, background: subtle, overflow:'hidden' }}>
            <div style={{ height:'100%', borderRadius:3, width:`${(progress.done / progress.total) * 100}%`, background:'#3498db' }} />
          </div>
        </div>
      )}

      {side === 'right' && (
        <div style={{ marginTop:10, paddingTop:8, borderTop:`1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          display:'flex', gap:10, flexWrap:'wrap', fontSize:12, color:T.textSub }}>
          <span>
            {doneRecord?.movedByName
              ? <>Moved by <span style={{ color:T.text, fontWeight:600 }}>{doneRecord.movedByName}</span> · {fmtWhen(doneRecord.movedAt)}</>
              : <>Moved when the checklist was submitted{doneRecord?.movedAt ? ` · ${fmtWhen(doneRecord.movedAt)}` : ''}</>}
          </span>
          {checklistRecord && (
            <span>
              Checklist ✓ {checklistRecord.itemCount} items
              {checklistRecord.attentionCount > 0 && ` · ${checklistRecord.attentionCount} need attention`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Timers the shop stopped automatically, for a manager to confirm or correct
// (2 Oct). Shown on My Workbench only to people who see every tech's work, and
// only while there is something to look at. Correcting the end time rewrites
// that tech's time in Lightspeed.
function AutoStopReview({ sessions, T, isDark, onDone }) {
  const [editing, setEditing] = useState(null);   // session id
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  if (!sessions.length) return null;
  const fmt = (d) => new Date(d).toLocaleString('en-US', { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' });
  // <input type="datetime-local"> wants local "YYYY-MM-DDTHH:MM".
  const toLocalInput = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };

  const send = async (s, endedAt) => {
    setBusy(s.id); setError('');
    try {
      const r = await fetch(`${API}/api/timer/sessions/${s.id}/review`, {
        method:'POST', credentials:'include', headers: authHeaders(true),
        body: JSON.stringify(endedAt ? { endedAt } : {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'That did not save.');
      if (data.written === false) setError(`Saved, but Lightspeed did not take the new time: ${data.writeError}. Use Retry on the bench.`);
      setEditing(null);
      onDone(s.id);
    } catch (err) { setError(err.message); }
    setBusy(null);
  };

  const btn = { fontSize:13, fontWeight:600, padding:'7px 12px', borderRadius:8, cursor:'pointer', fontFamily:'inherit' };
  return (
    <div style={{ marginBottom:16, padding:'14px 16px', borderRadius:12,
      background: isDark ? '#2a2210' : '#fdf3df', border:`1px solid ${isDark ? '#5a4520' : '#ecd29a'}` }}>
      <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>
        {sessions.length === 1 ? '1 timer was' : `${sessions.length} timers were`} stopped automatically
      </div>
      <div style={{ fontSize:13, color:T.textSub, marginBottom:10 }}>
        Someone forgot to press Stop. Check the end time is right, or correct it; the time in Lightspeed follows.
      </div>
      {error && <div role="alert" style={{ fontSize:13, color:'#e74c3c', marginBottom:8 }}>{error}</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {sessions.map(s => (
          <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', fontSize:13, color:T.text,
            padding:'8px 10px', borderRadius:8, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)' }}>
            <span style={{ fontWeight:700, minWidth:120 }}>{s.name}</span>
            <span style={{ fontWeight:600 }}>SVC-{s.workorderId}</span>
            <span style={{ color:T.textSub }}>started {fmt(s.startedAt)}</span>
            {editing === s.id ? (
              <span style={{ display:'inline-flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
                <label style={{ color:T.textSub }}>ended
                  <input type="datetime-local" value={value} onChange={e => setValue(e.target.value)}
                    style={{ marginLeft:6, background:T.input, color:T.text, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'5px 8px', fontFamily:'inherit' }} />
                </label>
                <button disabled={busy === s.id || !value} onClick={() => send(s, new Date(value).toISOString())}
                  style={{ ...btn, background:'#2a7ab8', color:'#fff', border:'none' }}>{busy === s.id ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setEditing(null)} style={{ ...btn, background:'transparent', color:T.textSub, border:`1px solid ${T.inputBorder}` }}>Cancel</button>
              </span>
            ) : (
              <>
                <span style={{ color:T.textSub }}>stopped {fmt(s.endedAt)}</span>
                <span style={{ display:'inline-flex', gap:8, marginLeft:'auto' }}>
                  <button disabled={busy === s.id} onClick={() => send(s, null)}
                    style={{ ...btn, background:'transparent', color:T.text, border:`1px solid ${T.inputBorder}` }}>{busy === s.id ? '…' : 'Looks right'}</button>
                  <button onClick={() => { setEditing(s.id); setValue(toLocalInput(s.endedAt)); }}
                    style={{ ...btn, background:'transparent', color:'#3498db', border:'1px solid #3498db66' }}>Change end time</button>
                </span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// The background picker: a small button by Refresh that opens five swatches.
// Light mode only (2 Oct). Saved to the person's login; cached on this
// computer too, so the page does not flash plain while it loads.
function BackgroundPicker({ value, onChange, T }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!e.target.closest('[data-bg-picker]')) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div data-bg-picker style={{ position:'relative' }}>
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{ ...S.btn(false), padding:'5px 14px', color:T.textSub, borderColor:T.filterBorder, background: T.card }}>
        🎨 Background
      </button>
      {open && (
        <div role="dialog" aria-label="Choose a background"
          style={{ position:'absolute', right:0, top:'calc(100% + 8px)', zIndex:50, background:'#fff', border:'1px solid #e0e0d8',
            borderRadius:14, padding:'14px 16px', boxShadow:'0 14px 34px rgba(0,0,0,0.18)', width:'max-content' }}>
          <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#777', marginBottom:12 }}>Background</div>
          <div style={{ display:'flex', gap:12 }}>
            {WORKBENCH_BACKGROUNDS.map(b => {
              const on = b.id === value;
              return (
                <button key={b.id} type="button" onClick={() => { onChange(b.id); setOpen(false); }} aria-pressed={on}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'transparent', border:'none', padding:0, cursor:'pointer', fontFamily:'inherit' }}>
                  <span style={{ width:96, height:60, borderRadius:10, boxSizing:'border-box',
                    border: on ? '3px solid #2a7ab8' : '1px solid #d0d0c8',
                    background: b.thumb ? `url(${b.thumb}) center / ${b.thumbSize || 'cover'}` : '#f0f0eb' }} />
                  <span style={{ fontSize:12, color:'#333', fontWeight: on ? 700 : 500 }}>{b.name}</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize:12, color:'#777', marginTop:12 }}>Yours only. It follows your login to any computer.</div>
        </div>
      )}
    </div>
  );
}

export default function TechView({ theme = 'dark', onRegisterNavHandler, currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  // Fails closed, unlike the nav: this controls whose work you can see, so an
  // absent permission map must not expose every tech's numbers. owner and
  // manager get it from ROLE_DEFAULTS; tech and associate via the User
  // Management checkbox.
  const canViewAll = !!currentUser?.permissions?.canViewAllTechs;
  const myName = currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : null;
  const [tickets, setTickets] = useState([]);
  const [finishedTickets, setFinishedTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedTech, setSelectedTech] = useState(canViewAll || !myName ? 'all' : myName);
  const [templates, setTemplates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);
  // The ticket open in the full-screen bench view. Kept while a checklist is
  // open from the bench, so closing the checklist returns there.
  const [benchTicket, setBenchTicket] = useState(null);
  const [checklistProgress, setChecklistProgress] = useState({});
  // Submitted checklists, by ticket. Shown on a Completed card as a summary;
  // no longer what puts a card there.
  const [completedRecords, setCompletedRecords] = useState({});
  // The board itself: which tickets were dragged to Completed (and by whom),
  // and which have a timer running. From /api/workbench.
  const [board, setBoard] = useState({ done: {}, running: {}, paused: {} });
  const boardRef = useRef(board);
  boardRef.current = board;
  // A card dropped on Completed while a timer runs on it waits here for an
  // answer: stop the timer and move it, or leave it in progress.
  const [pendingMove, setPendingMove] = useState(null); // { t, mine, others }
  const [pendingBusy, setPendingBusy] = useState(false);
  // Lightspeed's status order, for sorting the cards the way the Service Queue does.
  const [statusOrder, setStatusOrder] = useState({});
  const [moveError, setMoveError] = useState('');
  // Auto-stopped timers waiting for a manager (people who see every tech only).
  const [toReview, setToReview] = useState([]);
  const [techFinalizationStatuses, setTechFinalizationStatuses] = useState([]);
  // Hide Finished (3 Oct): Finished cards stay on the board by default, since at
  // some shops Finished means "done, waiting for pickup". Sticky on this
  // computer, like the status checkboxes beside it.
  const [hideFinished, setHideFinished] = useState(() => {
    try { return localStorage.getItem('spinboard-techview-hide-finished') === '1'; } catch (_) { return false; }
  });
  const toggleHideFinished = () => setHideFinished(v => {
    try { localStorage.setItem('spinboard-techview-hide-finished', v ? '0' : '1'); } catch (_) {}
    return !v;
  });
  const [statusFilters, setStatusFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem('spinboard-techview-statuses') || 'null') || []; }
    catch { return []; }
  });
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  // Board, My journal, or Today (managers). Remembered on this computer, so a
  // tech who lives in the journal lands there (Tech Mode part 2, 2 Oct).
  const [tab, setTabState] = useState(() => {
    let saved = null;
    try { saved = localStorage.getItem('spinboard-workbench-tab'); } catch (_) {}
    return saved === 'journal' || (saved === 'today' && canViewAll) ? saved : 'board';
  });
  const setTab = (t) => { setTabState(t); try { localStorage.setItem('spinboard-workbench-tab', t); } catch (_) {} };
  // This person's background (light mode only). Cached on this computer so the
  // page opens on it; the server copy wins once it arrives.
  const [bgId, setBgId] = useState(() => { try { return localStorage.getItem('spinboard-workbench-bg') || 'plain'; } catch (_) { return 'plain'; } });
  useEffect(() => {
    fetch(`${API}/api/workbench/background`, { credentials:'include', headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.background) { setBgId(d.background); try { localStorage.setItem('spinboard-workbench-bg', d.background); } catch (_) {} } })
      .catch(() => {});
  }, []);
  const chooseBackground = (id) => {
    setBgId(id);
    try { localStorage.setItem('spinboard-workbench-bg', id); } catch (_) {}
    fetch(`${API}/api/workbench/background`, { method:'PUT', credentials:'include', headers: authHeaders(true), body: JSON.stringify({ background: id }) }).catch(() => {});
  };
  const bg = isDark ? backgroundById('plain') : backgroundById(bgId);
  // Toolbar, stats and column headings on frosted glass over any background:
  // small text straight on a pattern or a photo is hard to read.
  const glass = bg.layer ? GLASS : null;
  // Bumped by Refresh on the journal and Today tabs, which load their own data.
  const [tabReload, setTabReload] = useState(0);

  const completedLoadedRef = useRef(false);

  // silent: refresh the data without dropping the view into its loading
  // state. Used when the view is already on screen and worth looking at —
  // rows update in place instead of the whole thing flashing empty.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // recentlyFinished moved out of /api/tickets on Aug 15 so the Service
      // Queue stops paying for it. Tech View was already fetching in parallel,
      // so picking it up as a fourth request here costs nothing — and it fails
      // independently now: a broken Completed column no longer takes the whole
      // Tech View down with it.
      const [ticketsRes, templatesRes, statusesRes, , recentRes, boardRes] = await Promise.all([
        fetch(`${API}/api/tickets`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/templates`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/workorder-statuses`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/tickets/status-meta`, { credentials:'include', headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .then(meta => { if (Array.isArray(meta)) setStatusOrder(Object.fromEntries(meta.map(m => [String(m.name || '').toLowerCase(), parseInt(m.sortOrder ?? 99999, 10)]))); })
          .catch(() => {}),
        fetch(`${API}/api/tickets/recently-finished`, { credentials:'include', headers: authHeaders() })
          .then(r => r.ok ? r.json() : { recentlyFinished: [] })
          .catch(() => ({ recentlyFinished: [] })),
        fetch(`${API}/api/workbench`, { credentials:'include', headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null),
      ]);
      if (boardRes) setBoard({ done: boardRes.done || {}, running: boardRes.running || {}, paused: boardRes.paused || {} });
      if (canViewAll) {
        fetch(`${API}/api/timer/review`, { credentials:'include', headers: authHeaders() })
          .then(r => r.ok ? r.json() : { sessions: [] })
          .then(d => setToReview(d.sessions || []))
          .catch(() => {});
      }

      const allTickets = ticketsRes.tickets || [];
      setTickets(allTickets);
      setFinishedTickets(recentRes.recentlyFinished || []);
      setTemplates(Array.isArray(templatesRes) ? templatesRes : []);
      setStatuses(Array.isArray(statusesRes) ? statusesRes : []);

      // Only load completedRecords from server on first mount
      if (!completedLoadedRef.current) {
        completedLoadedRef.current = true;
        try {
          const completedRes = await fetch(`${API}/api/completed-checklists`, { credentials:'include', headers: authHeaders() }).then(r => r.json());
          setCompletedRecords(completedRes || {});
        } catch (_) {}
      }

      const techNames = [...new Set(allTickets.map(t => t.tech).filter(Boolean).filter(t => t !== 'Unassigned'))].sort();
      setEmployees(techNames);

      // Single bulk call instead of N parallel fetches.
      // Returns { ticketId: checklistData, ... } for all tickets at once.
      try {
        const progressRes = await fetch(`${API}/api/checklists`, { credentials:'include', headers: authHeaders() });
        const progressMap = await progressRes.json();
        setChecklistProgress(progressMap || {});
      } catch (_) {
        setChecklistProgress({});
      }
    } catch (err) {
      console.error('TechView load error:', err);
    }
    setLoading(false);
  }, [canViewAll]);

  // Auto-detect best matching template for a ticket based on its line items
  const autoDetectTemplate = useCallback(async (ticket) => {
    if (templates.length <= 1) return templates[0] || null;
    const templatesWithSource = templates.filter(t => t.sourceItemId);
    if (templatesWithSource.length === 0) return null;
    try {
      const res = await fetch(`${API}/api/tickets/${ticket.id}`, { credentials:'include', headers: authHeaders() });
      const data = await res.json();
      const lineItemIds = new Set((data.lineItems || []).map(l => l.itemID).filter(Boolean));
      const match = templatesWithSource.find(t => lineItemIds.has(t.sourceItemId));
      return match || null;
    } catch (err) { console.error('autoDetect error:', err); return null; }
  }, [templates]);

  

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(s => { if (s.techFinalizationStatuses) setTechFinalizationStatuses(s.techFinalizationStatuses); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!onRegisterNavHandler) return;
    if (activeChecklist) {
      onRegisterNavHandler({ fn: () => setActiveChecklist(null) });
    } else if (benchTicket) {
      onRegisterNavHandler({ fn: () => setBenchTicket(null) });
    } else {
      onRegisterNavHandler(null);
    }
    return () => onRegisterNavHandler(null);
  }, [activeChecklist, benchTicket, onRegisterNavHandler]);

  // Run checklist from the bench: resume the template already in progress,
  // else the auto-detected one, else the only one -- the same choices the
  // card's own buttons make.
  const runChecklistFor = async (ticket) => {
    if (templates.length === 0) return;
    const saved = checklistProgress[ticket.id];
    const savedTpl = saved?.templateId ? templates.find(tp => tp.id === saved.templateId) : null;
    if (savedTpl) { setActiveChecklist({ ticket, template: savedTpl, readOnly: false, autoDetected: false }); return; }
    if (templates.length === 1) { setActiveChecklist({ ticket, template: templates[0], readOnly: false, autoDetected: false }); return; }
    const detected = await autoDetectTemplate(ticket);
    setActiveChecklist({ ticket, template: detected || templates[0], readOnly: false, autoDetected: !!detected });
  };

  useEffect(() => {
    if (!showStatusFilter) return;
    const handler = (e) => {
      if (!e.target.closest('[data-status-filter]')) setShowStatusFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStatusFilter]);

  const toggleStatusFilter = (statusName) => {
    const updated = statusFilters.includes(statusName)
      ? statusFilters.filter(s => s !== statusName)
      : [...statusFilters, statusName];
    setStatusFilters(updated);
    localStorage.setItem('spinboard-techview-statuses', JSON.stringify(updated));
  };

  const clearStatusFilters = () => {
    setStatusFilters([]);
    localStorage.setItem('spinboard-techview-statuses', JSON.stringify([]));
  };

  const baseList = selectedTech === 'all' ? tickets : tickets.filter(t => t.tech === selectedTech);

  const getProgress = (ticketId) => {
    const cl = checklistProgress[ticketId];
    if (!cl?.items?.length) return null;
    const done = cl.items.filter(i => i.status === 'pass' || i.status === 'attention').length;
    return { done, total: cl.items.length };
  };

  // In Completed because someone dragged it there (helpers/workbench.js).
  const isComplete = (ticketId) => !!board.done[String(ticketId)];

  // ── Dragging cards between the columns ──────────────────────────────────
  //
  // Pointer events rather than HTML drag-and-drop, which does nothing for a
  // finger on most tablets. A mouse drags as soon as it moves; a finger has to
  // rest on the card for a moment first, so an ordinary swipe still scrolls
  // the page. A tap that never became a drag falls through to the card's own
  // click and opens the bench.
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [drag, setDrag] = useState(null); // { t, from, x, y, offX, offY, w, over }

  const columnAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const col = el && el.closest ? el.closest('[data-drop-column]') : null;
    return col ? col.getAttribute('data-drop-column') : null;
  };

  const beginDrag = (st, x, y) => {
    st.started = true;
    setDrag({ t: st.t, from: st.from, x, y, offX: st.startX - st.rect.left, offY: st.startY - st.rect.top,
      w: st.rect.width, over: st.from });
  };

  const onCardPointerDown = (e, t, side) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const st = { t, from: side === 'right' ? 'done' : 'progress', startX: e.clientX, startY: e.clientY,
      pointerType: e.pointerType, rect: e.currentTarget.getBoundingClientRect(), started: false, moved: false };
    dragRef.current = st;
    if (e.pointerType !== 'mouse') {
      st.timer = setTimeout(() => {
        if (dragRef.current === st && !st.moved) beginDrag(st, st.startX, st.startY);
      }, 300);
    }
  };

  // Dropped in the other column. Shown straight away; put back if the server
  // says no. Changes nothing in Lightspeed.
  const moveTicket = useCallback(async (t, to) => {
    const id = String(t.id);
    let before;
    setMoveError('');
    setBoard(prev => {
      before = prev.done[id];
      const done = { ...prev.done };
      if (to === 'done') done[id] = { movedBy: currentUser?.id || null, movedByName: myName || currentUser?.username || null, movedAt: new Date().toISOString() };
      else delete done[id];
      return { ...prev, done };
    });
    try {
      const r = await fetch(`${API}/api/workbench/${encodeURIComponent(id)}/done`, {
        method: to === 'done' ? 'POST' : 'DELETE', credentials:'include', headers: authHeaders(true),
      });
      if (!r.ok) throw new Error();
      if (to === 'done') {
        const rec = await r.json().catch(() => null);
        if (rec) setBoard(prev => ({ ...prev, done: { ...prev.done, [id]: { movedBy: rec.movedBy, movedByName: rec.movedByName, movedAt: rec.movedAt } } }));
      }
    } catch (_) {
      setBoard(prev => {
        const done = { ...prev.done };
        if (before) done[id] = before; else delete done[id];
        return { ...prev, done };
      });
      setMoveError(`Could not move ${t.displayId}. Check the connection and try again.`);
    }
  }, [currentUser, myName]);

  useEffect(() => {
    const onMove = (e) => {
      const st = dragRef.current;
      if (!st) return;
      const dist = Math.hypot(e.clientX - st.startX, e.clientY - st.startY);
      if (!st.started) {
        if (st.pointerType === 'mouse') { if (dist > 6) beginDrag(st, e.clientX, e.clientY); else return; }
        else { if (dist > 10) { st.moved = true; clearTimeout(st.timer); dragRef.current = null; } return; }
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, over: columnAt(e.clientX, e.clientY) } : d);
    };
    const finish = (e, cancelled) => {
      const st = dragRef.current;
      dragRef.current = null;
      if (!st) return;
      clearTimeout(st.timer);
      if (!st.started) return;
      // The click that follows a drop is not a tap: do not open the bench.
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 400);
      const to = cancelled ? null : columnAt(e.clientX, e.clientY);
      setDrag(null);
      if (!to || to === st.from) return;
      // Into Completed with a timer still running on it: ask first.
      const running = to === 'done' ? (boardRef.current.running[String(st.t.id)] || []) : [];
      if (running.length) {
        const me = String(currentUser?.id ?? '');
        setPendingMove({
          t: st.t,
          mine: running.some(r => String(r.userId) === me),
          others: running.filter(r => String(r.userId) !== me).map(r => r.name),
        });
        return;
      }
      moveTicket(st.t, to);
    };
    const onUp = (e) => finish(e, false);
    const onCancel = (e) => finish(e, true);
    // While a finger is dragging a card, the page must not scroll under it.
    const onTouchMove = (e) => { if (dragRef.current && dragRef.current.started) e.preventDefault(); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [moveTicket, currentUser]);

  // "Stop the timer and move it": stop the caller's own timer, which writes
  // their time to Lightspeed, then move the card. Anyone else's timer has to be
  // stopped by them, so the card stays put while one is still running.
  const confirmStopAndMove = async () => {
    const pm = pendingMove;
    if (!pm || !pm.mine || pm.others.length) { setPendingMove(null); return; }
    setPendingBusy(true);
    try {
      const r = await fetch(`${API}/api/timer/ticket/${encodeURIComponent(pm.t.id)}/stop`, {
        method:'POST', credentials:'include', headers: authHeaders(true), body: '{}',
      });
      if (!r.ok) throw new Error();
      setBoard(prev => { const running = { ...prev.running }; delete running[String(pm.t.id)]; return { ...prev, running }; });
      setPendingMove(null);
      moveTicket(pm.t, 'done');
    } catch (_) {
      setPendingMove(null);
      setMoveError(`Could not stop the timer on ${pm.t.displayId}, so it stays in progress. Try again from the bench.`);
    }
    setPendingBusy(false);
  };

  const openBench = (t) => {
    if (suppressClickRef.current) return;
    setBenchTicket(t);
  };

  // Status filter applies to In Progress only — completed tickets always visible
  const inProgress = baseList.filter(t => !isComplete(t.id) && (statusFilters.length === 0 || statusFilters.includes(t.status)));
  // Completed (3 Oct): open tickets dragged there, plus Finished ones unless
  // "Hide Finished" is ticked. A ticket closed out at the register (Done &
  // Paid, Finished/Paid, Invoiced: anything paid) always leaves the board; the
  // journal keeps the record. Paid tickets used to linger here, a leftover from
  // when a submitted checklist moved the card.
  const isPaidStatus = (st) => /paid|invoiced/i.test(String(st || ''));
  const completedFromActive = baseList.filter(t => isComplete(t.id));
  const finishedBase = selectedTech === 'all' ? finishedTickets : finishedTickets.filter(t => t.tech === selectedTech);
  const completedFinished = hideFinished ? [] : finishedBase.filter(t => isComplete(t.id) && !isPaidStatus(t.status)
    && !completedFromActive.find(c => String(c.id) === String(t.id)));
  // Same order as the Service Queue's default (Lightspeed) view: by the shop's
  // status order in Lightspeed, then oldest drop-off first.
  const byQueueOrder = (a, b) => {
    const oa = statusOrder[String(a.status || '').toLowerCase()] ?? 99999;
    const ob = statusOrder[String(b.status || '').toLowerCase()] ?? 99999;
    if (oa !== ob) return oa - ob;
    return new Date(a.timeIn || 0) - new Date(b.timeIn || 0);
  };
  inProgress.sort(byQueueOrder);
  const completed = [...completedFromActive, ...completedFinished].sort(byQueueOrder);
  // Everyone's tech time per card (8 Oct).
  const timeTotals = useTimeTotals([...inProgress, ...completed].map(t => t.id));

  if (activeChecklist) {
    return (
      <Checklist
        ticket={activeChecklist.ticket}
        template={activeChecklist.template}
        templates={templates}
        statuses={statuses}
        theme={theme}
        techFinalizationStatuses={techFinalizationStatuses}
        readOnly={activeChecklist.readOnly || false}
        autoDetected={activeChecklist.autoDetected || false}
        onSwitchTemplate={(newTemplateId) => {
          const newTpl = templates.find(t => t.id === newTemplateId);
          if (newTpl) setActiveChecklist({ ...activeChecklist, template: newTpl, autoDetected: false });
        }}
        onClose={() => { setActiveChecklist(null); setTimeout(() => load(true), 500); }}
        onSave={(data) => {
          setChecklistProgress(prev => ({ ...prev, [activeChecklist.ticket.id]: data }));
          // If submitted, immediately update completed records in local state
          if (data.submitted) {
            const record = {
              ticketId: activeChecklist.ticket.id,
              templateName: activeChecklist.template.name,
              tech: activeChecklist.ticket.tech,
              itemCount: data.items?.length || 0,
              attentionCount: data.items?.filter(i => i.status === 'attention').length || 0,
              completedAt: new Date().toISOString(),
            };
            setCompletedRecords(prev => {
              const next = { ...prev, [activeChecklist.ticket.id]: record };
              return next;
            });
          }
        }}
      />
    );
  }

  if (benchTicket) {
    return (
      <BenchView
        ticket={benchTicket}
        theme={theme}
        currentUser={currentUser}
        backLabel="My Workbench"
        onClose={() => { setBenchTicket(null); load(true); }}
        onRunChecklist={runChecklistFor}
      />
    );
  }

  return (
    <div style={{ position:'relative' }}>
      {/* The background: fixed behind the content area (the sidebar is 220px
          wide), so it stays put while the cards scroll. */}
      {bg.layer && (
        <div aria-hidden="true" style={{ position:'fixed', top:0, left:220, right:0, bottom:0, zIndex:0, pointerEvents:'none', ...bg.layer }} />
      )}
      <div style={{ position:'relative', zIndex:1 }}>
      {/* Header + filters in one toolbar */}
      <div style={{ ...S.topbar, background: bg.layer ? 'transparent' : T.bg, flexWrap:'wrap', gap:12,
        ...(glass ? { ...glass, padding:'12px 16px', marginBottom:16 } : {}) }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
          <div style={{ ...S.pageTitle, color:T.text }}>My Workbench</div>
          <div role="tablist" aria-label="My Workbench views"
            style={{ display:'flex', gap:4, background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:4, marginLeft:8 }}>
            {[['board', 'My Board'], ['journal', 'My Journal'], ...(canViewAll ? [['today', 'Today']] : [])].map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                style={{ border:'none', borderRadius:7, padding:'7px 14px', fontSize:14, fontWeight:600, fontFamily: DISPLAY_FONT, cursor:'pointer',
                  background: tab === key ? '#2a7ab8' : 'transparent', color: tab === key ? '#fff' : T.textSub }}>
                {label}
              </button>
            ))}
          </div>
          {tab !== 'board' ? null : canViewAll ? <>
            <span style={{ fontSize:12, color:T.textSub, marginLeft:8 }}>Tech:</span>
            <select
              value={selectedTech}
              onChange={e => setSelectedTech(e.target.value)}
              style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'6px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', cursor:'pointer' }}
            >
              <option value="all">All Technicians</option>
              {employees.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </> : myName && (
            <span style={{ fontSize:12, color:T.textSub, marginLeft:8 }}>Viewing: <strong>{myName}</strong></span>
          )}
        </div>
        {tab !== 'board' && <WorkbenchClock T={T} />}
        {!isDark && <BackgroundPicker value={bgId} onChange={chooseBackground} T={T} />}
        <button style={{ ...S.btn(false), padding:'5px 14px', color:T.textSub, borderColor:T.filterBorder }}
          onClick={() => { completedLoadedRef.current = false; load(tab !== 'board'); if (tab !== 'board') setTabReload(n => n + 1); }}>↻ Refresh</button>
      </div>

      {tab === 'journal' ? (
        <SurfaceContext.Provider value={glass}>
          <WorkJournal key={tabReload} T={T} isDark={isDark} myName={myName}
            tickets={tickets} finishedTickets={finishedTickets} board={board} onOpenTicket={setBenchTicket} />
        </SurfaceContext.Provider>
      ) : tab === 'today' && canViewAll ? (
        <SurfaceContext.Provider value={glass}>
          <TodayBoard key={tabReload} T={T} isDark={isDark} tickets={tickets}
            onOpenTicket={setBenchTicket} onReview={() => setTab('board')} />
        </SurfaceContext.Provider>
      ) : (<>
      <div style={glass ? { ...glass, padding:'14px 16px 0', marginBottom:20 } : undefined}>

      {/* Status checkboxes — always visible */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${T.border}` }}>
        <span style={{ fontSize:11, color:T.textSub, marginRight:4, whiteSpace:'nowrap' }}>Statuses included in view:</span>
        {[...new Set(tickets.map(t => t.status))].sort().map(status => {
          const active = statusFilters.includes(status);
          return (
            <label key={status} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer',
              padding:'4px 10px', borderRadius:6,
              background: active ? '#3498db22' : 'transparent',
              border: `1px solid ${active ? '#3498db' : T.filterBorder}`,
              transition:'all 0.15s', userSelect:'none',
            }}>
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggleStatusFilter(status)}
                style={{ cursor:'pointer', accentColor:'#3498db', width:12, height:12 }}
              />
              <span style={{ fontSize:12, color: active ? '#3498db' : T.textSub, fontWeight: active ? 500 : 400 }}>{status}</span>
            </label>
          );
        })}
        <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', padding:'4px 10px', borderRadius:6, marginLeft:8,
          background: hideFinished ? '#2ecc7122' : 'transparent', border:`1px solid ${hideFinished ? '#2ecc71' : T.filterBorder}`, userSelect:'none' }}>
          <input type="checkbox" checked={hideFinished} onChange={toggleHideFinished}
            style={{ cursor:'pointer', accentColor:'#2ecc71', width:12, height:12 }} />
          <span style={{ fontSize:12, color: hideFinished ? '#1e8449' : T.textSub, fontWeight: hideFinished ? 600 : 400 }}>Hide Finished</span>
        </label>
        {statusFilters.length > 0 && (
          <button onClick={clearStatusFilters} style={{ fontSize:11, color:'#e74c3c', background:'transparent', border:`1px solid #e74c3c33`, borderRadius:6, padding:'4px 10px', cursor:'pointer' }}>
            Clear
          </button>
        )}
      </div>

      {pendingMove && (
        <div onClick={() => !pendingBusy && setPendingMove(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2100, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div role="dialog" aria-modal="true" aria-label="Timer still running" onClick={e => e.stopPropagation()}
            style={{ width:'100%', maxWidth:520, background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:16, padding:'24px 26px',
              boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ fontFamily: DISPLAY_FONT, fontSize:22, fontWeight:700, color:T.text, marginBottom:8 }}>
              {pendingMove.others.length ? 'A timer is still running' : 'Your timer is still running'}
            </div>
            <div style={{ fontSize:16, color:T.textSub, lineHeight:1.5, marginBottom:20 }}>
              {pendingMove.others.length
                ? <>{pendingMove.others.join(' and ')}{pendingMove.others.length > 1 ? ' have timers' : ' has a timer'} running on {pendingMove.t.displayId}. It has to be stopped from the bench before this card can move to Completed, so it stays in progress.</>
                : <>Stop your timer on {pendingMove.t.displayId} and move it to Completed? Your time is written to Lightspeed when it stops. If you keep it running, the card stays in progress.</>}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', flexWrap:'wrap' }}>
              {pendingMove.others.length ? (
                <button onClick={() => setPendingMove(null)}
                  style={{ ...S.btn(true), background:'#2a7ab8', color:'#fff', fontSize:15, padding:'12px 22px', minHeight:48 }}>OK</button>
              ) : (
                <>
                  <button onClick={() => setPendingMove(null)} disabled={pendingBusy}
                    style={{ ...S.btn(false), color:T.text, borderColor:T.cardBorder, fontSize:15, padding:'12px 20px', minHeight:48 }}>Keep it running</button>
                  <button onClick={confirmStopAndMove} disabled={pendingBusy}
                    style={{ ...S.btn(true), background:'#c0392b', color:'#fff', fontSize:15, padding:'12px 20px', minHeight:48, opacity: pendingBusy ? 0.7 : 1 }}>
                    {pendingBusy ? 'Stopping…' : 'Stop timer and move'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {canViewAll && (
        <AutoStopReview sessions={toReview} T={T} isDark={isDark}
          onDone={(id) => setToReview(prev => prev.filter(x => x.id !== id))} />
      )}

      {moveError && (
        <div role="alert" style={{ marginBottom:12, padding:'10px 14px', borderRadius:8, fontSize:13, fontWeight:600,
          color:'#e74c3c', background: isDark ? '#2a1010' : '#fdecea', border:'1px solid #e74c3c55' }}>{moveError}</div>
      )}

      {/* Stats */}
      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        {[
          { label:'Open Tickets',  value: baseList.length,   color: T.text },
          { label:'In Progress',   value: inProgress.length, color: '#F4622A' },
          { label:'Completed',     value: completed.length,  color: '#2ecc71' },
          // Past due (3 Oct, replaced Templates, which was setup information):
          // cards still in progress whose promised date has gone. Red when any.
          { label:'Past Due',      value: inProgress.filter(t => t.isOverdue).length,
            color: inProgress.some(t => t.isOverdue) ? '#e74c3c' : T.textSub },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'12px 20px', minWidth:130 }}>
            <div style={{ fontSize:10, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:26, fontWeight:700, color, fontFamily:'Arial, sans-serif' }}>{value}</div>
          </div>
        ))}
        <WorkbenchClock T={T} />
      </div>
      </div>

      {loading ? (
        <div style={{ color:T.textSub, fontSize:13, padding:40, textAlign:'center' }}>Loading tickets…</div>
      ) : baseList.length === 0 ? (
        <div style={{ color:T.textSub, fontSize:13, padding:40, textAlign:'center' }}>No tickets found</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 3px 1fr', gap:0, alignItems:'stretch' }}>
          {/* The cards are raised tiles, a shade darker than the page on light and
              a shade lighter on dark, with a lip of light along the top edge and
              a shadow that grows on hover, so they read as things to pick up
              (2 Oct). A green ring while a timer runs on one. */}
          <style>{`
            @keyframes sbTimerPulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.25 } }
            .wb-card {
              cursor: grab;
              background: ${isDark ? 'linear-gradient(180deg, #243149 0%, #1b263b 100%)' : 'linear-gradient(180deg, #e6e6df 0%, #d9d9d1 100%)'};
              border: 1px solid ${isDark ? '#2e3d58' : '#c8c8be'};
              box-shadow: inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.75)'},
                0 1px 2px rgba(0,0,0,${isDark ? 0.4 : 0.12}), 0 4px 10px rgba(0,0,0,${isDark ? 0.3 : 0.10});
              transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.15s;
            }
            .wb-card:hover, .wb-card:focus-visible {
              transform: translateY(-2px);
              box-shadow: inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.85)'},
                0 2px 4px rgba(0,0,0,${isDark ? 0.45 : 0.14}), 0 10px 22px rgba(0,0,0,${isDark ? 0.4 : 0.16});
            }
            .wb-card:active { cursor: grabbing; }
            /* Completed cards: tinted green and a little quieter, so done reads
               as done at a glance (2 Oct). */
            .wb-card.wb-done {
              background: ${isDark ? 'linear-gradient(180deg, #1d3a33 0%, #17302b 100%)' : 'linear-gradient(180deg, #dcebdf 0%, #cfe2d4 100%)'};
              border-color: ${isDark ? '#2a5446' : '#b5d1bc'};
            }
            .wb-card.wb-running { border-color: #2ecc71; }
            /* Paused: an amber edge, quieter than the running ring (2 Oct). */
            .wb-card.wb-paused { border-color: #e67e22aa; box-shadow: inset 3px 0 0 #e67e22,
              0 1px 2px rgba(0,0,0,${isDark ? 0.4 : 0.12}), 0 4px 10px rgba(0,0,0,${isDark ? 0.3 : 0.10}); }
            .wb-card.wb-running, .wb-card.wb-running:hover {
              box-shadow: 0 0 0 2px #2ecc7144, 0 4px 12px rgba(0,0,0,${isDark ? 0.35 : 0.12});
            }
          `}</style>

          {[
            { key:'progress', side:'left', label:'In Progress', color:'#F4622A', list: inProgress,
              empty: 'Nothing in progress' },
            { key:'done', side:'right', label:'Completed', color:'#2ecc71', list: completed,
              empty: 'Drag a card here when it is off your plate' },
          ].map((col, idx) => {
            const isTarget = !!drag && drag.over === col.key && drag.from !== col.key;
            const column = (
              <div key={col.key} data-drop-column={col.key}
                style={{ [col.side === 'left' ? 'paddingRight' : 'paddingLeft']: 20, minHeight: 240,
                  borderRadius: 12, transition: 'background 0.15s, outline-color 0.15s',
                  background: isTarget ? `${col.color}14` : 'transparent',
                  outline: `2px dashed ${drag && drag.from !== col.key ? (isTarget ? col.color : (isDark ? '#2e3d58' : '#d0d0c8')) : 'transparent'}`,
                  outlineOffset: 6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, paddingBottom:10, borderBottom:`2px solid ${col.color}33`,
                  ...(glass ? { ...glass, padding:'8px 12px', borderBottom:`2px solid ${col.color}` } : {}) }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:col.color, flexShrink:0 }} />
                  <div style={{ fontSize:13, fontWeight:700, color:col.color, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                    {col.label}
                  </div>
                  <div style={{ fontSize:12, color:col.color, background:`${col.color}22`, borderRadius:10, padding:'2px 10px', fontWeight:600 }}>{col.list.length}</div>
                </div>
                {col.list.length === 0 ? (
                  <div style={{ background:T.card, border:`1px dashed ${T.cardBorder}`, borderRadius:10, padding:'32px 24px', textAlign:'center', color:T.textSub, fontSize:14 }}>
                    {col.empty}
                  </div>
                ) : (
                  <div style={{ display:'grid', gap:10, gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))' }}>
                    {col.list.map(t => (
                      <WorkbenchCard
                        key={t.id} t={t}
                        progress={getProgress(t.id)}
                        checklistData={checklistProgress[t.id] || null}
                        checklistRecord={completedRecords[t.id] || null}
                        doneRecord={board.done[String(t.id)] || null}
                        running={board.running[String(t.id)] || null}
                        paused={board.paused[String(t.id)] || null}
                        timeTotal={timeTotals[String(t.id)] || null}
                        side={col.side}
                        dragging={!!drag && String(drag.t.id) === String(t.id)}
                        T={T} isDark={isDark}
                        onOpen={openBench}
                        onPointerDown={onCardPointerDown}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
            return idx === 0 ? [column,
              <div key="sep" style={{ background: isDark ? '#243050' : '#d0d0c8', alignSelf:'stretch', margin:'0 4px' }} />] : column;
          })}

          {drag && (
            <div aria-hidden="true" style={{ position:'fixed', left: drag.x - drag.offX, top: drag.y - drag.offY, width: drag.w,
              pointerEvents:'none', zIndex:2000, transform:'rotate(1.5deg)', boxShadow:'0 18px 40px rgba(0,0,0,0.45)', borderRadius:10 }}>
              <WorkbenchCard t={drag.t} progress={getProgress(drag.t.id)} checklistData={checklistProgress[drag.t.id] || null}
                checklistRecord={completedRecords[drag.t.id] || null} doneRecord={board.done[String(drag.t.id)] || null}
                running={board.running[String(drag.t.id)] || null} paused={board.paused[String(drag.t.id)] || null} side={drag.from === 'done' ? 'right' : 'left'}
                dragging={false} T={T} isDark={isDark} onOpen={() => {}} onPointerDown={() => {}} />
            </div>
          )}
        </div>
      )}
      </>)}
      </div>
    </div>
  );
}
